import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { ODataLinkedService } from 'app/model/linkedservice/odata.linkedservice';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel, passwordOrAKVtoSecretBase } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { Constants } from 'app/shared/shared-constants';
import { oDataAuthTypes, Credential, azureCloudTypeDef, isLogicAppExpression } from 'shell-models-lib';
import { GlobalVariables } from 'app/services/globals';
import { ValueAdapter } from '../value.adapter';
import { ReferenceAdapter } from '../reference.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ODataLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                url: '*',
                authenticationType: '*',
                userName: '*',
                password: 'secret',
                tenant: '*',
                aadResourceId: '*',
                aadServicePrincipalCredentialType: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret',
                servicePrincipalEmbeddedCert: 'secret',
                servicePrincipalEmbeddedCertPassword: 'secret',
                encryptedCredential: '*',
                authHeaders: '*', // each value item is in securestring type,
                azureCloudType: '*',
                credential: GlobalVariables.enableConnectorUserAuthCred ? '*' : undefined
            },
            parameters: '*'
        }
    }
})
export class ODataLinkedServiceAdapter extends LinkedServiceAdapter<ODataLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => ODataLinkedService {
        return ODataLinkedService;
    }

    protected model(): IModel {
        return ODataLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: ODataLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.ODataLinkedService;

        properties.type = Metamodel.modelFor(ODataLinkedService).name;
        let userName: string;
        let password: string | Model.SecretBase;
        let servicePrincipalId: string;
        let servicePrincipalKey: string | Model.SecretBase;
        let tenant: string;
        let servicePrincipalEmbeddedCert: string | Model.SecretBase;
        let servicePrincipalEmbeddedCertPassword: string | Model.SecretBase;
        let aadResourceId: string;
        let aadServicePrincipalCredentialType: string;
        let authenticationType: string = linkedService.authenticationType;
        let azureCloudType: azureCloudTypeDef;
        let credential: Model.CredentialReference;

        const url: string = this.valueToResource(linkedService, ODataLinkedService.URL);
        if (linkedService.authenticationType === oDataAuthTypes.AADServicePrincipalWithCert || linkedService.authenticationType === oDataAuthTypes.AADServicePrincipalWithKey) {
            authenticationType = oDataAuthTypes.AADServicePrincipal;
            aadServicePrincipalCredentialType = linkedService.authenticationType;
            servicePrincipalId = this.valueToResource(linkedService, ODataLinkedService.SERVICE_PRINCIPAL_ID);
            tenant = this.valueToResource(linkedService, ODataLinkedService.TENANT);
            aadResourceId = this.valueToResource(linkedService, ODataLinkedService.AAD_RESOURCE_ID);
            if (aadServicePrincipalCredentialType === oDataAuthTypes.AADServicePrincipalWithKey) {
                servicePrincipalKey = parameterizablePasswordOrAKVtoSecretBase(linkedService, ODataLinkedService.SERVICE_PRINCIPAL_KEY, this.context);
            } else if (aadServicePrincipalCredentialType === oDataAuthTypes.AADServicePrincipalWithCert) {
                servicePrincipalEmbeddedCert = parameterizablePasswordOrAKVtoSecretBase(linkedService, ODataLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, this.context);
                servicePrincipalEmbeddedCertPassword = parameterizablePasswordOrAKVtoSecretBase(linkedService, ODataLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, this.context);
            }
            azureCloudType = linkedService.azureCloudType;
        } else if (linkedService.authenticationType === oDataAuthTypes.BasicAuthentication || linkedService.authenticationType === oDataAuthTypes.WindowsAuthentication) {
            userName = this.valueToResource(linkedService, ODataLinkedService.USER_NAME);
            password = parameterizablePasswordOrAKVtoSecretBase(linkedService, ODataLinkedService.PASSOWRD, this.context);
        } else if (linkedService.authenticationType === oDataAuthTypes.MSIAuthentication) {
            aadResourceId = this.valueToResource(linkedService, ODataLinkedService.AAD_RESOURCE_ID);
        } else if (GlobalVariables.enableConnectorUserAuthCred && linkedService.authenticationType === oDataAuthTypes.UserAuth) {
            credential = linkedService.credential
                ? ReferenceAdapter.toResource(linkedService.credential, this.context) as Model.CredentialReference
                : undefined;
        }

        properties.typeProperties = {
            url,
            authenticationType,
            userName,
            password,
            tenant,
            aadResourceId,
            aadServicePrincipalCredentialType,
            azureCloudType,
            servicePrincipalId,
            servicePrincipalKey,
            servicePrincipalEmbeddedCert,
            servicePrincipalEmbeddedCertPassword,
            credential
        };

        if (!arrayIsNullOrEmpty(linkedService.authHeaders)) {
            properties.typeProperties.authHeaders = {};
            linkedService.authHeaders.forEach((pair) => {
                if (pair.value != null) {
                    properties.typeProperties.authHeaders[pair.key] = passwordOrAKVtoSecretBase(pair.value, this.context);
                }
            });
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(modelLinkedServiceResource: Model.LinkedServiceResource, linkedService: ODataLinkedService): Promise<ODataLinkedService> {
        const prop = modelLinkedServiceResource.properties as Model.ODataLinkedService;
        const typeProperties = prop.typeProperties;

        ValueAdapter.toModel(linkedService, ODataLinkedService.URL, typeProperties.url);
        linkedService.authenticationType = typeProperties.authenticationType;
        if (typeProperties.authenticationType === oDataAuthTypes.AADServicePrincipal) {
            linkedService.authenticationType = typeProperties.aadServicePrincipalCredentialType;
        }
        ValueAdapter.toModel(linkedService, ODataLinkedService.USER_NAME, typeProperties.userName);
        parameterizableSecretBaseToModel(linkedService, ODataLinkedService.PASSOWRD, typeProperties.password, this.context,
            typeProperties.authenticationType === oDataAuthTypes.BasicAuthentication || typeProperties.authenticationType === oDataAuthTypes.WindowsAuthentication);
        ValueAdapter.toModel(linkedService, ODataLinkedService.TENANT, typeProperties.tenant);
        ValueAdapter.toModel(linkedService, ODataLinkedService.AAD_RESOURCE_ID, typeProperties.aadResourceId);
        ValueAdapter.toModel(linkedService, ODataLinkedService.SERVICE_PRINCIPAL_ID, typeProperties.servicePrincipalId);
        parameterizableSecretBaseToModel(linkedService, ODataLinkedService.SERVICE_PRINCIPAL_KEY, typeProperties.servicePrincipalKey, this.context,
            linkedService.authenticationType === oDataAuthTypes.AADServicePrincipalWithKey);
        parameterizableSecretBaseToModel(linkedService, ODataLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT, typeProperties.servicePrincipalEmbeddedCert, this.context,
            linkedService.authenticationType === oDataAuthTypes.AADServicePrincipalWithCert);
        parameterizableSecretBaseToModel(linkedService, ODataLinkedService.SERVICE_PRINCIPAL_EMBEDDED_CERT_PASSWORD, typeProperties.servicePrincipalEmbeddedCertPassword, this.context,
            linkedService.authenticationType === oDataAuthTypes.AADServicePrincipalWithCert);
        linkedService.azureCloudType = typeProperties.azureCloudType as azureCloudTypeDef;
        if (GlobalVariables.enableConnectorUserAuthCred) {
            linkedService.credential = ReferenceAdapter.toProxyModel(Metamodel.modelFor(Credential), typeProperties.credential, this.context);
        }

        if (typeProperties.authHeaders) {
            linkedService.authHeaders = [];
            Object.keys(typeProperties.authHeaders).forEach((key) => {
                if (typeProperties.authHeaders[key] != null) {
                    linkedService.authHeaders.push({ key, value: Constants.maskedCredential });
                }
            });
        }
        return linkedService;
    }

    protected fullyByJsonEditorCustomizer(linkedServiceResource: Model.LinkedServiceResource): boolean {
        const prop = linkedServiceResource.properties as Model.ODataLinkedService;
        return prop?.typeProperties?.authHeaders && (Object.values(prop.typeProperties.authHeaders).some((v) => isLogicAppExpression(v as unknown as string)));
    }
}
